import React, { useEffect, useRef, useState } from 'react';
import type { VisionAuthorship, VisionDraft, VisionGeneration } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { DateTime } from 'luxon';
import Button from '../button/Button';
import Textarea from '../input/Textarea';
import Alert from '../alert/Alert';
import EditButton from '../../global/buttons/EditButton';
import { GLOBAL_ICONS } from '../../util/vars';
import { VisionGenerationInfo } from './VisionGenerationInfo';
import { splitVision } from './actionSteps';

export const MAX_VISION_LENGTH = 300;
export const DRAFT_POLL_MS = 2000;

export interface VisionDraftApi {
  create: () => Promise<VisionDraft>;
  get: (id: number) => Promise<VisionDraft>;
  cancel: (id: number) => Promise<void>;
}

interface Props {
  vision: string;
  visionGeneration?: VisionGeneration;
  visionAuthorship?: VisionAuthorship;
  goalTitles: string[];
  readOnly?: boolean;
  // Omit the "Our vision" label when a surrounding container already titles the vision.
  hideLabel?: boolean;
  drafts?: VisionDraftApi;
  onSaveVision?: (vision: string, draftId?: number) => void;
}

type Mode =
  | { kind: 'view' }
  | { kind: 'writing'; text: string }
  | { kind: 'generating'; draft: VisionDraft | null }
  | { kind: 'draft'; draft: VisionDraft; text: string }
  | { kind: 'failed' };

const P = 'pages.VISION_EDITOR';

const VisionQuote = ({ vision }: { vision: string }): JSX.Element => {
  const { lead, body } = splitVision(vision);
  return (
    <blockquote className={'max-w-3xl'}>
      <span aria-hidden className={'block font-serif text-6xl leading-none text-header h-8'}>&ldquo;</span>
      <p className={'text-xl font-medium leading-normal whitespace-pre-wrap'}>{lead}</p>
      {body && <p className={'mt-3 text-sm leading-relaxed whitespace-pre-wrap'}>{body}</p>}
    </blockquote>
  );
};

const Credit = ({ authorship, generation }: { authorship?: VisionAuthorship; generation?: VisionGeneration }): JSX.Element | null => {
  const { translate: t } = useI18n();
  if ('ai' === authorship?.source) return <VisionGenerationInfo generation={generation} />;
  if (!authorship?.editedByName) return null;
  const date = authorship.editedAt ? DateTime.fromISO(authorship.editedAt).toFormat('d LLL yyyy') : '';
  return (
    <p className={'text-xs text-gray-500'}>
      {date ? t(`${P}.WRITTEN_BY_ON`, { name: authorship.editedByName, date }) : t(`${P}.WRITTEN_BY`, { name: authorship.editedByName })}
    </p>
  );
};

type StepState = 'done' | 'active' | 'waiting' | 'warning';

const StepIcon = ({ state }: { state: StepState }): JSX.Element => {
  if ('done' === state) return <span aria-hidden className={'flex-shrink-0 w-4 h-4 rounded-full bg-positive text-white text-xs leading-4 text-center'}>✓</span>;
  if ('warning' === state) return <span aria-hidden className={'flex-shrink-0 w-4 h-4 rounded-full bg-warning text-white text-xs leading-4 text-center font-bold'}>!</span>;
  if ('active' === state) return <span aria-hidden className={'flex-shrink-0 w-4 h-4 rounded-full border-2 border-purple-600 animate-spin'} style={{ borderRightColor: 'transparent' }} />;
  return <span aria-hidden className={'flex-shrink-0 w-4 h-4 rounded-full border border-gray-300'} />;
};

const usePlural = () => {
  const { translate: t } = useI18n();
  return (key: string, n: number, params: Record<string, string | number> = {}): string => t(1 === n ? `${P}.${key}_ONE` : `${P}.${key}`, { ...params, count: n });
};

const Steps = ({ draft, goalCount }: { draft: VisionDraft | null; goalCount: number }): JSX.Element => {
  const { translate: t } = useI18n();
  const plural = usePlural();
  const stage = draft?.stage ?? 'queued';
  const order = [ 'queued', 'researching', 'reading_goals', 'drafting', 'done' ];
  const at = order.indexOf(stage);
  const website: StepState = at >= 2 ? (false === draft?.websiteReached ? 'warning' : 'done') : 'active';
  const goals: StepState = at >= 3 ? 'done' : 2 === at ? 'active' : 'waiting';
  const drafting: StepState = at >= 4 ? 'done' : 3 === at ? 'active' : 'waiting';
  const steps: { state: StepState; label: string }[] = [
    {
      state: website,
      label: 'warning' === website ? t(`${P}.STEP_WEBSITE_FAILED`)
        : 'done' === website ? t(`${P}.STEP_RESEARCHED`, { host: draft?.websiteHost ?? '' }) : t(`${P}.STEP_RESEARCHING`),
    },
    { state: goals, label: plural('done' === goals ? 'STEP_READ' : 'STEP_READING', goalCount) },
    { state: drafting, label: t(`${P}.STEP_DRAFTING`) },
  ];
  return (
    <ol className={'space-y-1.5 text-xs'}>
      {steps.map((step, index) => (
        <li key={index} data-state={step.state} className={`flex items-center gap-2 ${'active' === step.state ? 'font-medium' : ''} ${'waiting' === step.state ? 'text-gray-400' : 'warning' === step.state ? 'text-warning' : ''}`}>
          <StepIcon state={step.state} />
          {step.label}
        </li>
      ))}
    </ol>
  );
};

const GeneratingBox = ({ title, draft, goalCount, showSteps, onCancel }: {
  title: string; draft: VisionDraft | null; goalCount: number; showSteps: boolean; onCancel?: () => void;
}): JSX.Element => {
  const { translate: t } = useI18n();
  return (
    <div className={'space-y-2'}>
      <div role={'status'} className={'border border-purple-200 bg-purple-50 rounded-default p-4 space-y-3'}>
        <div className={'flex items-center gap-2 text-sm font-medium'}>
          <span aria-hidden className={'text-purple-600'}>✦</span>
          {title}
          {onCancel && <span className={'ml-auto'}><Button type={'button'} size={'sm'} asLink onClick={onCancel}>{t(`${P}.CANCEL`)}</Button></span>}
        </div>
        <div aria-hidden className={'space-y-2 animate-pulse'}>
          <div className={'h-2.5 rounded-full bg-purple-100 w-11/12'} />
          <div className={'h-2.5 rounded-full bg-purple-100 w-10/12'} />
          <div className={'h-2.5 rounded-full bg-purple-100 w-7/12'} />
        </div>
        {showSteps && <Steps draft={draft} goalCount={goalCount} />}
      </div>
      <p className={'text-xs text-gray-400'}>{t(`${P}.TAKES_A_MINUTE`)}</p>
    </div>
  );
};

const OptionCard = ({ icon, title, hint, onClick, disabled, primary }: {
  icon: string; title: string; hint: string; onClick: () => void; disabled?: boolean; primary?: boolean;
}): JSX.Element => (
  <button
    type={'button'}
    onClick={onClick}
    disabled={disabled}
    className={`text-left rounded-default p-3 ${primary ? 'border-2 border-purple-600 bg-purple-50 hover:bg-purple-100' : 'border border-gray-300 bg-white hover:bg-gray-50'} disabled:opacity-50 disabled:cursor-not-allowed`}
  >
    <span className={'block text-sm font-semibold'}><span aria-hidden className={primary ? 'text-purple-600' : ''}>{icon}</span> {title}</span>
    <span className={'block mt-1 text-xs text-gray-500'}>{hint}</span>
  </button>
);

const VisionEditor = ({ vision, visionGeneration, visionAuthorship, goalTitles, readOnly = false, hideLabel = false, drafts, onSaveVision }: Props): JSX.Element => {
  const { translate: t } = useI18n();
  const plural = usePlural();
  const [ mode, setMode ] = useState<Mode>({ kind: 'view' });
  const hasVision = !!vision.trim();
  const goalCount = goalTitles.length;
  const canDraft = !!drafts && goalCount > 0;
  const backgroundRunning = !hasVision && [ 'pending', 'running' ].includes(visionGeneration?.status ?? '');
  const draftId = 'generating' === mode.kind ? mode.draft?.id : undefined;
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    if (!drafts || undefined === draftId) return undefined;
    const timer = window.setInterval(() => {
      drafts.get(draftId).then((draft) => {
        if (!alive.current) return;
        setMode((current) => {
          if ('generating' !== current.kind || current.draft?.id !== draft.id) return current;
          if ('completed' === draft.status && draft.vision) return { kind: 'draft', draft, text: draft.vision };
          if ('failed' === draft.status) return { kind: 'failed' };
          if ('cancelled' === draft.status) return { kind: 'view' };
          return { kind: 'generating', draft };
        });
      }).catch(() => undefined);
    }, DRAFT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [ drafts, draftId ]);

  const startDraft = () => {
    if (!drafts) return;
    setMode({ kind: 'generating', draft: null });
    drafts.create().then((draft) => {
      if (alive.current) setMode((current) => ('generating' === current.kind ? { kind: 'generating', draft } : current));
    }).catch(() => {
      if (alive.current) setMode({ kind: 'failed' });
    });
  };

  const cancelDraft = () => {
    if (drafts && undefined !== draftId) drafts.cancel(draftId).catch(() => undefined);
    setMode({ kind: 'view' });
  };

  const save = (text: string, id?: number) => {
    onSaveVision?.(text.trim(), id);
    setMode({ kind: 'view' });
  };

  const header = (action?: React.ReactNode) => {
    if (hideLabel) return action ? <div className={'flex justify-end'}>{action}</div> : null;
    return (
      <div className={'flex justify-between items-start gap-4'}>
        <p className={'uppercase text-xs font-semibold tracking-widest text-header'}>{t('pages.IMPLEMENTATION_PLAN.OUR_VISION')}</p>
        {action}
      </div>
    );
  };

  let body: React.ReactNode;
  let action: React.ReactNode = null;

  if (readOnly || 'view' === mode.kind) {
    if (hasVision) {
      if (!readOnly) action = <EditButton type={'button'} onClick={() => setMode({ kind: 'writing', text: vision })}>{t('pages.IMPLEMENTATION_PLAN.EDIT_VISION')}</EditButton>;
      body = (
        <>
          <VisionQuote vision={vision} />
          <Credit authorship={visionAuthorship} generation={visionGeneration} />
        </>
      );
    } else if (backgroundRunning) {
      body = <GeneratingBox title={t(`${P}.DRAFTING`)} draft={null} goalCount={goalCount} showSteps={false} />;
    } else if (readOnly) {
      body = <p className={'text-gray-500'}>{t('pages.IMPLEMENTATION_PLAN.NO_VISION')}</p>;
    } else {
      body = (
        <div className={'border border-dashed border-gray-300 bg-gray-50 rounded-default p-4 space-y-3'}>
          <p className={'text-sm text-gray-600'}>{t(`${P}.HELPER`)}</p>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
            <OptionCard primary icon={'✦'} title={t(`${P}.DRAFT_FOR_ME`)} hint={canDraft ? t(`${P}.DRAFT_FOR_ME_HINT`) : t(`${P}.ADD_GOAL_FIRST`)} onClick={startDraft} disabled={!canDraft} />
            <OptionCard icon={'✎'} title={t(`${P}.WRITE_MY_OWN`)} hint={t(`${P}.WRITE_MY_OWN_HINT`)} onClick={() => setMode({ kind: 'writing', text: '' })} />
          </div>
          {goalCount > 0 && <p className={'text-xs text-gray-400'}>{t(`${P}.USES`, { goals: goalTitles.join(', ') })}</p>}
        </div>
      );
    }
  } else if ('writing' === mode.kind) {
    body = (
      <div className={'space-y-3'}>
        <Textarea
          id={'vision'}
          label={t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.VISION')}
          hideLabel
          rows={4}
          maxLength={MAX_VISION_LENGTH}
          placeholder={t(`${P}.PLACEHOLDER`)}
          value={mode.text}
          onChange={(event) => setMode({ kind: 'writing', text: event.target.value.slice(0, MAX_VISION_LENGTH) })}
        />
        <div className={'flex justify-between items-center gap-3 text-xs text-gray-400'}>
          {canDraft ? (
            <button type={'button'} className={'font-medium text-purple-600 hover:underline'} onClick={startDraft}>
              ✦ {hasVision ? t(`${P}.SUGGEST_REWRITE`) : t(`${P}.DRAFT_INSTEAD`)}
            </button>
          ) : <span />}
          <span>{t(`${P}.COUNTER`, { count: mode.text.length, max: MAX_VISION_LENGTH })}</span>
        </div>
        <div className={'flex items-center justify-end gap-3'}>
          <Button type={'button'} asLink onClick={() => setMode({ kind: 'view' })}>{t(`${P}.CANCEL`)}</Button>
          <Button type={'button'} color={'positive'} disabled={!mode.text.trim()} onClick={() => save(mode.text)}>{t(`${P}.SAVE`)}</Button>
        </div>
      </div>
    );
  } else if ('generating' === mode.kind) {
    body = <GeneratingBox title={hasVision ? t(`${P}.DRAFTING_REWRITE`) : t(`${P}.DRAFTING`)} draft={mode.draft} goalCount={mode.draft?.goalCount ?? goalCount} showSteps onCancel={cancelDraft} />;
  } else if ('draft' === mode.kind) {
    const { draft, text } = mode;
    const draftGoals = draft.goalCount;
    body = (
      <div className={'space-y-3'}>
        <Alert show={false === draft.websiteReached} type={'warning'} disableIcon={false}>
          <p className={'text-sm'}>{t(`${P}.WEBSITE_FAILED_NOTICE`)}</p>
        </Alert>
        {hasVision && (
          <div className={'border border-gray-200 bg-gray-50 rounded-default px-4 py-3'}>
            <p className={'uppercase text-xs font-semibold tracking-wider text-gray-400'}>{t(`${P}.CURRENT_VISION`)}</p>
            <p className={'mt-1 text-sm text-gray-500'}>{vision}</p>
          </div>
        )}
        <div className={'border-2 border-purple-600 bg-purple-50 rounded-default px-4 py-3 space-y-2'}>
          <span className={'inline-block text-xs font-semibold tracking-wide text-purple-600 bg-white border border-purple-200 rounded px-2 py-0.5'}>✦ {t(`${P}.DRAFT_LABEL`)}</span>
          <Textarea
            id={'vision-draft'}
            label={t(`${P}.DRAFT_LABEL`)}
            hideLabel
            rows={3}
            maxLength={MAX_VISION_LENGTH}
            value={text}
            className={'appearance-none w-full bg-transparent border-transparent p-0 text-sm'}
            onChange={(event) => setMode({ kind: 'draft', draft, text: event.target.value.slice(0, MAX_VISION_LENGTH) })}
          />
        </div>
        <p className={'text-xs text-gray-500'}>
          {draft.websiteReached && draft.websiteHost
            ? plural('DRAFTED_FROM_SITE', draftGoals, { host: draft.websiteHost })
            : plural('DRAFTED_FROM_GOALS', draftGoals)}
        </p>
        <div className={'flex items-center gap-3'}>
          <Button type={'button'} color={'positive'} disabled={!text.trim()} onClick={() => save(text, draft.id)}>{t(`${P}.USE_THIS`)}</Button>
          <Button type={'button'} isOutline icon={GLOBAL_ICONS.reload} onClick={startDraft}>{t(`${P}.TRY_AGAIN`)}</Button>
          <span className={'ml-auto'}>
            <Button type={'button'} asLink onClick={() => setMode({ kind: 'view' })}>{hasVision ? t(`${P}.KEEP_CURRENT`) : t(`${P}.DISCARD`)}</Button>
          </span>
        </div>
      </div>
    );
  } else {
    body = (
      <div className={'space-y-3'}>
        <Alert show type={'error'} disableIcon={false}>
          <p className={'text-sm'}>{t(`${P}.FAILED`)}</p>
        </Alert>
        <div className={'flex items-center gap-3'}>
          <Button type={'button'} isOutline icon={GLOBAL_ICONS.reload} onClick={startDraft} disabled={!canDraft}>{t(`${P}.TRY_AGAIN`)}</Button>
          <span className={'ml-auto'}><Button type={'button'} asLink onClick={() => setMode({ kind: 'view' })}>{t(`${P}.CANCEL`)}</Button></span>
        </div>
      </div>
    );
  }

  return (
    <div className={'space-y-3'}>
      {header(action)}
      {body}
    </div>
  );
};

export { VisionEditor };
